import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';
import { FlowStep } from './flowblueprint.service';
import { FlowInstance } from './flow.instance.service';

export interface FlowActivity {
  Id: string;
  ExternalId: string;
  Name: string;
  Description: string | null;
  DisplayStatus: string;
  InputSchema: InputSchemaDto | null;
  Steps: FlowStep[];
  CategoryId?: string;
}

export interface InputSchemaDto {
  Properties: InputPropertyDto[];
}

export interface InputPropertyDto {
  Name: string;
  Type: string;
  Description: string | null;
  Required: boolean;
}


@Injectable({
  providedIn: 'root'
})
export class FlowActivityService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Add request interceptor to dynamically set baseURL and include OAuth token
      this.api.interceptors.request.use((config) => {
        // Dynamically construct baseURL on each request to get current tenantId
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getFlowActivities(): Promise<Array<FlowActivity> | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/activity');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowActivity(id: string): Promise<FlowActivity | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/activity/${id}`);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateFlowActivity(flowActivity: FlowActivity): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/activity/${flowActivity.Id}`, flowActivity);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createFlowActivity(flowActivity: FlowActivity): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post('/activity', flowActivity);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteFlowActivity(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/activity/${id}`);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowInstanceArchive(integrationId: string, instanceId: string): Promise<FlowInstance[]> {
    if (!this.api) return [];

    try {
      const response = await this.api.get(
        `${integrationId}/flow/archive/${instanceId}`,
        { params: { includeHistory: true } }
      );
      const archiveData = response.data;

      const items = Array.isArray(archiveData?.History)
        ? archiveData.History
        : Array.isArray(archiveData?.history)
          ? archiveData.history
          : Array.isArray(archiveData)
            ? archiveData
            : [];

      return items
        .map((item: Record<string, unknown>) => {
          const instance = (item?.['FlowInstance'] ?? item) as FlowInstance | null;
          if (!instance) return null;
          return {
            ...instance,
            ArchiveId: (item?.['Id'] as string) ?? instance.ArchiveId
          } as FlowInstance;
        })
        .filter((item: FlowInstance | null): item is FlowInstance => !!item);
    } catch (error: unknown) {
      console.error('Failed to load flow instance archive:', error);
      return [];
    }
  }
}
